// The pieces of a status row that BOTH the project Status page and a specialist's
// tab render. Shared so the two surfaces state the same thing the same way - the
// status a page shows and the status behind a click must agree (principle 23).
import { Link } from "react-router-dom";

import type {
  Application,
  Counterparty,
  OnTrack,
  VerdictItem,
} from "../api/hooks/useAgentPortfolio";
import type { NextMove } from "../api/hooks/useProjectAgentStatus";
import { projectSpecialistPath } from "../routes/learningAgentPaths";
import { formatDay, formatShortDay } from "../utils/attributeFormat";
import { projectItems } from "../utils/nextActions";
import type { SpecialistLists } from "./StatusCardParts";
import { Tooltip } from "./Tooltip";
import styles from "./PortfolioRowParts.module.css";
import { NOT_APPLICABLE } from "../utils/standDown";
import {
  BULLET,
  CLOSED_STAGES,
  LEAD_LABEL,
  type RoutePoint,
  splitPoint,
} from "../utils/optionRoutes";

// Who owes the next move. Each agent has its own owners (RowVocabulary.owners in
// inrange_agents/portfolio_row.py), and every one reads as us, nobody, or someone
// we are waiting on.
const ownerTone = (who: string): string => {
  if (who === "US") return styles.ownerUs;
  if (who === "NOBODY") return styles.ownerClear;
  return styles.ownerThem;
};

const ownerLabel = (who: string): string => {
  if (who === "US") return "Ball with us";
  if (who === "NOBODY") return "Nobody owes";
  return `Waiting on ${who}`;
};

export const LifecycleChip = ({ lifecycle }: { lifecycle: string }) =>
  lifecycle ? (
    <span
      className={
        lifecycle === NOT_APPLICABLE ? `${styles.lifecycle} ${styles.lifecycleNeutral}` : styles.lifecycle
      }
    >
      {lifecycle}
    </span>
  ) : (
    <span className={styles.unset}>lifecycle not set</span>
  );

// Where a route or an application stands: neutral while nothing has come back, the
// lifecycle's colour once the network has moved on it, muted once it has closed.
const stageTone = (stage: string): string => {
  if (stage === "APPLIED") return ` ${styles.lifecycleNeutral}`;
  if (CLOSED_STAGES.includes(stage)) return ` ${styles.stageClosed}`;
  return "";
};

export const StageChip = ({ stage }: { stage: string }) => (
  <span className={`${styles.lifecycle}${stageTone(stage)}`}>{stage}</span>
);

/** ``overdueFrom``: a reply that party owes is overdue, so the move is our chase of it.
 * ``move``: the action behind the chip, whose due date follows "Ball with us". */
export const OwnerChip = ({
  who,
  lifecycle,
  overdueFrom = "",
  move,
}: {
  who: string;
  lifecycle: string | undefined;
  overdueFrom?: string;
  move: Pick<NextMove, "due" | "title"> | undefined;
}) =>
  lifecycle === NOT_APPLICABLE ? null : overdueFrom ? (
    <span className={`${styles.owner} ${styles.ownerThem} ${styles.ownerNamed}`}>
      Overdue from {overdueFrom}
    </span>
  ) : who ? (
    who === "US" && move?.due ? (
      <Tooltip content={`Due ${formatDay(move.due)}: ${move.title}`}>
        <span className={styles.ownerWithDue}>
          <span className={`${styles.owner} ${ownerTone(who)}`}>
            {ownerLabel(who)}
          </span>
          <span className={styles.dateChip}>
            due {formatShortDay(move.due)}
          </span>
        </span>
      </Tooltip>
    ) : (
      <span className={`${styles.owner} ${ownerTone(who)}`}>
        {ownerLabel(who)}
      </span>
    )
  ) : (
    <span className={styles.unset}>owner not set</span>
  );

// status_details is a scannable opening line, then paragraphs each led by an upper-case
// label and a colon (WHERE IT STANDS, Building readiness's areas). The labels are plain
// text, so the page lifts them itself, matched by SHAPE rather than a copy of the
// vocabulary: a list here would stop agreeing the day the prompt adds a label.
// A label written alone on its line without its colon ("WHERE IT STANDS").
const BARE_LABEL = /^[A-Z][A-Z0-9'/ -]{2,40}$/;

// The same label written as markdown ("- **Roof condition:** ..."), which rows
// stored before the prompt asked for plain capitals still carry. The colon is what
// makes it a label: a line that merely opens in bold ("**£1.2m** agreed") is not one.
const MARKDOWN_LABEL =
  /^(?:[-*•]\s+)?\*\*([^*:\n]{2,40}?)(?::\*\*|\*\*:)[ \t]*/;

// A route's detail in the options box keeps its label bold inside the bullet rather
// than lifted into a heading, so the details read as belonging to the route above.
export const RoutePointText = ({ point }: { point: RoutePoint }) =>
  point.label ? (
    <>
      <strong>{point.label}:</strong> {point.text}
    </>
  ) : (
    <>{point.text}</>
  );

const OptionPoint = ({ text }: { text: string }) => (
  <ul className={styles.points}>
    <li>
      <RoutePointText point={splitPoint(text)} />
    </li>
  </ul>
);

// A labelled line, wherever it sits. The prompt separates its paragraphs with a SINGLE
// newline, so splitting on blank lines alone would leave every label after the first
// mid-block.
// `inOptions`: only the options box lifts a bracketed stage into a chip (the
// backend checks stages in options and nowhere else) and keeps a bullet's label
// inside its bullet.
const LabelledLine = ({
  line,
  inOptions,
}: {
  line: string;
  inOptions: boolean;
}) => {
  const point = BULLET.exec(line);
  if (inOptions && point) return <OptionPoint text={line.slice(point[0].length)} />;
  const markdown = MARKDOWN_LABEL.exec(line);
  if (markdown) {
    const rest = line.slice(markdown[0].length);
    return (
      <p className={rest ? undefined : styles.labelOnly}>
        <strong className={styles.leadLabel}>
          {markdown[1].trim().toUpperCase()}
        </strong>
        {rest}
      </p>
    );
  }
  if (point) {
    return (
      <ul className={styles.points}>
        <li>{line.slice(point[0].length)}</li>
      </ul>
    );
  }
  if (BARE_LABEL.test(line)) {
    return (
      <p className={styles.labelOnly}>
        <strong className={styles.leadLabel}>{line}</strong>
      </p>
    );
  }
  const match = LEAD_LABEL.exec(line);
  if (!match) return <p>{line}</p>;
  const rest = line.slice(match[0].length);
  return (
    <p className={rest ? undefined : styles.labelOnly}>
      <strong className={styles.leadLabel}>
        {/* Outside the options box the brackets stay part of the label. */}
        {match[2] && !inOptions ? `${match[1]} (${match[2]})` : match[1]}
        {inOptions && match[2] && (
          <span className={styles.labelChip}>
            <StageChip stage={match[2]} />
          </span>
        )}
      </strong>
      {rest}
    </p>
  );
};

// Prose the agent writes with its own line structure: every line is its own
// paragraph, and a leading LABEL: is lifted onto a line of its own.
const LabelledProse = ({
  text,
  inOptions,
}: {
  text: string;
  inOptions: boolean;
}) => (
  <>
    {text
      .split("\n")
      .filter((line) => line.trim())
      .map((line, i) => (
        <LabelledLine key={i} line={line.trim()} inOptions={inOptions} />
      ))}
  </>
);

// A labelled line's label, upper-cased, or "" when it carries none.
const labelOf = (line: string): string => {
  const markdown = MARKDOWN_LABEL.exec(line);
  if (markdown) return markdown[1].trim().toUpperCase();
  if (BARE_LABEL.test(line)) return line;
  return LEAD_LABEL.exec(line)?.[1] ?? "";
};

// The status on the specialist's Status card, as written.
export const StatusForCard = ({
  text,
  omitLabels,
}: {
  text: string;
  /** Labelled lines a card on the same page already shows, in capitals ("STRUCTURAL"):
   * Building readiness's area lines, beside its area cards. */
  omitLabels: readonly string[];
}) => {
  if (!text.trim()) {
    return <p className={styles.muted}>No pass has written a status yet.</p>;
  }
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  return (
    <div className={styles.status}>
      {lines.map((line, i) => {
        const label = labelOf(line);
        if (omitLabels.includes(label)) return null;
        return <LabelledLine key={i} line={line} inOptions={false} />;
      })}
    </div>
  );
};

// Resolved from the project's lists, open items only: a closed one is no longer what the
// date rests on.
export const resolveVerdictItem = (
  onTrack: OnTrack,
  lists: SpecialistLists | null,
): { item: VerdictItem; href: string } | null => {
  if (!lists || !onTrack.item) return null;
  const items = projectItems(lists.data);
  const found = items.actions.get(onTrack.item);
  const action = found && !found.action.closed ? found : undefined;
  const date = items.dates.get(onTrack.item);
  const tracked = date && !date.date.closed ? date : undefined;
  const resolved: { agent: string; item: VerdictItem } | null = action
    ? {
        agent: action.agent,
        item: { kind: "action", title: action.action.title, date: "" },
      }
    : tracked
      ? {
          agent: tracked.agent,
          item: { kind: "date", title: tracked.date.what, date: tracked.date.date },
        }
      : null;
  if (!resolved) return null;
  return {
    item: resolved.item,
    href: `${projectSpecialistPath(lists.projectId, resolved.agent)}#${onTrack.item}`,
  };
};

/** The item under a verdict's reason: "Needed by <date>: <action>", or "<date>: <the
 * deadline>", linked to it. Without one, the bare date, as before items were named. */
const VerdictItemLine = ({
  onTrack,
  item,
  href,
}: {
  onTrack: OnTrack;
  item: VerdictItem | null;
  href: string;
}) => {
  if (!item) {
    return onTrack.by ? (
      <span className={styles.kind}> by {formatDay(onTrack.by)}</span>
    ) : null;
  }
  const when = item.kind === "date" ? item.date || onTrack.by : onTrack.by;
  const label =
    item.kind === "action"
      ? when
        ? `Needed by ${formatDay(when)}:`
        : "Needed:"
      : when
        ? `${formatDay(when)}:`
        : "";
  return (
    <span className={styles.verdictItem}>
      {label && (
        <strong className={onTrack.verdict ? styles[onTrack.verdict] : ""}>
          {label}
        </strong>
      )}{" "}
      <Link to={href}>{item.title}</Link>
    </span>
  );
};

/** Why the verdict, as one line under a card that shows the verdict as a chip, then
 * the item its date belongs to. */
export const VerdictReason = ({
  onTrack,
  found,
}: {
  onTrack: OnTrack;
  /** The item the date belongs to (resolveVerdictItem); null when it names none, the
   * lists are still loading, or the surface (the dashboard) leaves it out. */
  found: { item: VerdictItem; href: string } | null;
}) => {
  if (!onTrack.verdict || !onTrack.reason) return null;
  return (
    <p className={styles.reason}>
      <strong className={styles[onTrack.verdict]}>
        Why {VERDICT_LABEL[onTrack.verdict].toLowerCase()}:
      </strong>{" "}
      {onTrack.reason}
      <VerdictItemLine
        onTrack={onTrack}
        item={found?.item ?? null}
        href={found?.href ?? ""}
      />
    </p>
  );
};

// The routes on the table, as prose. The agent is asked to put each route on its
// own line, so honouring those line breaks is what turns it from a wall of text
// into something scannable; a row written before that instruction shipped renders
// as the single paragraph it was written as, until the next pass rewrites it.
export const OptionsOnTheTable = ({ text }: { text: string }) =>
  text.trim() ? (
    <div className={`${styles.status} ${styles.options}`}>
      <LabelledProse text={text} inOptions />
    </div>
  ) : (
    <p className={styles.muted}>
      Nothing recorded. An option is on the table once the network has priced
      it.
    </p>
  );

const kw = (value: number): string => `${value.toLocaleString("en-GB")} kW`;

// Every application and amendment, newest first: each amendment is its own entry,
// so the box reads as the history of what was asked for, current ask on top.
export const Applications = ({
  applications,
}: {
  applications: Application[];
}) => {
  if (applications.length === 0) {
    return (
      <p className={styles.muted}>
        None recorded yet. The next pass records every application it finds.
      </p>
    );
  }
  const ordered = [...applications].sort((a, b) =>
    b.date.localeCompare(a.date),
  );
  return (
    <ul className={styles.applications}>
      {ordered.map((app, i) => {
        const capacities = [
          app.pv_installed_kw !== null &&
            `${kw(app.pv_installed_kw)} PV installed`,
          app.battery_kw !== null && `${kw(app.battery_kw)} battery`,
          app.export_kw !== null && `${kw(app.export_kw)} export`,
        ].filter(Boolean);
        return (
          <li
            key={`${app.org}-${app.date}-${i}`}
            className={styles.application}
          >
            <p className={styles.applicationHead}>
              <strong>{app.org}</strong>
              {app.org_kind && (
                <span className={styles.sub}>{app.org_kind}</span>
              )}
              {app.date && (
                <span className={styles.dateChip}>{formatDay(app.date)}</span>
              )}
              <StageChip stage={app.stage} />
            </p>
            {capacities.length > 0 && <p>{capacities.join(" · ")}</p>}
            {app.references.length > 0 && (
              <p className={styles.sub}>Ref {app.references.join(" / ")}</p>
            )}
            {app.changed && (
              <p className={styles.sub}>Changed: {app.changed}</p>
            )}
          </li>
        );
      })}
    </ul>
  );
};

// The owner chip is NOT repeated here: every surface that renders this block
// already carries it in the panel header directly above.
// Whose move it is, as a sentence reads it: "with us", "with the DNO".
const VERDICT_LABEL: Record<Exclude<OnTrack["verdict"], "">, string> = {
  on_track: "On track",
  at_risk: "At risk",
  blocked: "Blocked",
};

const verdictTip = (onTrack: OnTrack): string =>
  onTrack.by
    ? `${onTrack.reason} (by ${formatDay(onTrack.by)})`
    : onTrack.reason;

/** The verdict as a word before a line, only where it is news: on track is the
 * default, and a word every site shares is one nobody reads. */
export const VerdictFlag = ({ onTrack }: { onTrack: OnTrack | undefined }) =>
  onTrack?.verdict === "at_risk" || onTrack?.verdict === "blocked" ? (
    <Tooltip content={verdictTip(onTrack)}>
      <strong className={styles[onTrack.verdict]}>
        {VERDICT_LABEL[onTrack.verdict]}
      </strong>
    </Tooltip>
  ) : null;

/** The verdict as a coloured chip, On track included, on a card that leads with it. */
export const VerdictChip = ({ onTrack }: { onTrack: OnTrack }) =>
  onTrack.verdict ? (
    <Tooltip content={verdictTip(onTrack)}>
      <span
        className={`${styles.verdictChip} ${styles[`${onTrack.verdict}Chip`]}`}
      >
        {VERDICT_LABEL[onTrack.verdict]}
      </span>
    </Tooltip>
  ) : null;

export interface ChainNode {
  org: string;
  org_kind: string;
  references: string[];
  people: Counterparty[];
}

// The roster merges by organisation AND person, so one operator legitimately
// arrives as several rows - a reference on one, a named contact on another. The
// chain is a picture of the parties, not of the rows, so they fold into one node
// per organisation. Keyed case-insensitively on the name, because the agent
// writes it as it appears in the correspondence and casing drifts between
// letters; org_kind and order come from the first row seen.
export const chainNodes = (counterparties: Counterparty[]): ChainNode[] => {
  const byOrg = new Map<string, ChainNode>();
  for (const cp of counterparties) {
    const key = cp.org.trim().toLowerCase();
    let node = byOrg.get(key);
    if (!node) {
      node = { org: cp.org, org_kind: cp.org_kind, references: [], people: [] };
      byOrg.set(key, node);
    }
    if (!node.org_kind && cp.org_kind) node.org_kind = cp.org_kind;
    const reference = cp.reference.trim();
    if (reference && !node.references.includes(reference)) {
      node.references.push(reference);
    }
    if (cp.person_name.trim() || cp.person_email.trim()) node.people.push(cp);
  }
  return [...byOrg.values()];
};

export const ConnectionChain = ({
  counterparties,
}: {
  counterparties: Counterparty[];
}) => {
  const nodes = chainNodes(counterparties);
  if (nodes.length === 0) {
    return <p className={styles.muted}>No counterparty recorded.</p>;
  }
  return (
    <div className={styles.chain}>
      {nodes.map((node) => (
        <div key={node.org} className={styles.node}>
          <span className={styles.nodeKind}>{node.org_kind || "party"}</span>
          <div className={styles.nodeOrg}>{node.org}</div>
          {node.references.map((reference) => (
            <div key={reference} className={styles.nodeRef}>
              {reference}
            </div>
          ))}
          {node.people.map((person, i) => (
            <div key={i} className={styles.nodePerson}>
              {person.person_name}
              {person.person_name && person.person_email ? " · " : ""}
              {person.person_email}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
